import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Rating,
  MenuItem,
  Pagination,
  IconButton,
  Select,
} from "@mui/material";
import { MdDelete } from "react-icons/md";
import DeleteFeedbackModal from "../Models/DeleteFeedbackModal";
import { GetUserFeedback } from "../Redux/Actions/AuthAction";
import { useDispatch, useSelector } from "react-redux";
import notfound from "../assets/images/not-found.png";
import Loader from "../components/Loader/Loading";
import useTitle from "../hooks/useTitle";
import SearchField from "../context/SearchField";

const FeedbackList = () => {
  useTitle("Feedbacks");
  const dispatch = useDispatch();
  const { feedback, loading } = useSelector((state) => state.Auth);
  const [page, setPage] = useState(1);
  const [cachedPages, setCachedPages] = useState({});
  const [search, setSearch] = useState("");
  const [filterRating, setFilterRating] = useState("");
  const [openfeedbackmodal, setopenfeedbackmodal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handledeletefeedback = (id) => {
    setopenfeedbackmodal(true);
    setSelectedFeedback(id);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setCachedPages({});
  }, [debouncedSearch, filterRating]);

  useEffect(() => {
    const scrollToTop = () => {
      const mainContent = document.getElementById("main-content");
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    if (debouncedSearch || filterRating) {
      dispatch(GetUserFeedback(page, debouncedSearch, filterRating)).then(
        (res) => {
          scrollToTop();
        }
      );
    } else {
      if (!cachedPages[page]) {
        dispatch(GetUserFeedback(page, debouncedSearch, filterRating)).then(
          (res) => {
            setCachedPages((prev) => ({
              ...prev,
              [page]: res?.data,
            }));
            scrollToTop();
          }
        );
      }
    }
  }, [dispatch, page, debouncedSearch, filterRating]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", lg: "center" },
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", width: { xs: "100%", md: "auto" } }}
          >
            User Feedbacks
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <SearchField search={search} setSearch={setSearch} />
            <Select
              value={filterRating}
              displayEmpty
              onChange={(e) => setFilterRating(e.target.value)}
              sx={{
                border: "1px solid #CACACA",
                borderRadius: "8px",
                height: "40px",
                fontSize: 14,
                background: "white",
                width: { xs: "100%", sm: "200px" },
                minWidth: "100px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-icon": { color: "#000" },
                "&:hover": { border: "1px solid #EF5A24" },
              }}
            >
              <MenuItem value="">All Ratings</MenuItem>
              {[5, 4, 3, 2, 1].map((star) => (
                <MenuItem key={star} value={star}>
                  {star} Stars
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        {loading ? (
          <div
            className="relative h-[70vh]"
          >
            <Loader />
          </div>
        ) : feedback?.data?.length > 0 ? (
          <div className="min-h-[78vh] overflow-y-auto">
            <Grid container spacing={2} sx={{ p: 2 }}>
              {feedback?.data?.map((user) => (
                <Grid key={user.id} item xs={12} sm={12} md={6} lg={4}>
                  <Card
                    sx={{
                      p: 2,
                      height: "100%",
                      position: "relative",
                      boxShadow: "rgba(65, 65, 65, 0.24) 0px 3px 8px",
                      borderRadius: "8px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={user?.User?.profileImage || "/default-avatar.png"}
                        sx={{ bgcolor: "#b5b5b5", width: 50, height: 50 }}
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {user?.User?.first_name} {user?.User?.last_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ pl: 0 }}>
                      <Rating
                        sx={{ color: "#FFD700" }}
                        defaultValue={user?.rating}
                        size="20"
                        name="half-rating-read"
                        readOnly
                      />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {user?.desc}
                      </Typography>
                    </CardContent>
                    <IconButton
                      onClick={() => handledeletefeedback(user?.id)}
                      sx={{
                        color: "#F1580C",
                        position: "absolute",
                        top: 5,
                        right: 5,
                      }}
                    >
                      <MdDelete size={20} />
                    </IconButton>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            <img
              src={notfound}
              alt="Not Found"
              style={{ width: "200px", height: "auto" }}
            />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
              No data found
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            width: "100%",
            display: "flex",
            justifyContent: "end",
            "@media (max-width:600px)": { mt: 2 },
          }}
        >
          <Pagination
            shape="rounded"
            count={feedback?.pagination?.totalPages}
            page={page}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                background:
                  "linear-gradient(to right, #EF5A24 1%, #F38157 99%)",
                color: "#fff",
              },
              "@media (max-width:960px)": {
                fontSize: "0.75rem",
                "& .MuiPaginationItem-root": {
                  minWidth: "auto",
                  px: "8px",
                  mx: "2px",
                },
              },
              "@media (max-width:600px)": {
                fontSize: "0.5rem",
                "& .MuiPaginationItem-root": {
                  px: "8px",
                  mx: "1px",
                },
              },
            }}
          />
        </Box>
      </Box>
      <DeleteFeedbackModal
        Open={openfeedbackmodal}
        page={page}
        setPage={setPage}
        cachedPages={cachedPages}
        setCachedPages={setCachedPages}
        setOpen={setopenfeedbackmodal}
        feedbackId={selectedFeedback}
        debouncedSearch={debouncedSearch}
        filterRating={filterRating}
      />
    </>
  );
};

export default FeedbackList;
